// @givmo/compliance — Filings: every filing the organization makes, grouped by kind (CCM-R1-INTAKE
// (a); D-CCM-21; frames 13, 13-390, 14, 15, 16, 16b and 17; DESIGN-NOTE-1b §1–§2).
//
// ══ WHAT THE PAGE SAYS, AND WHAT IT NEVER SAYS ═════════════════════════════════════════════════
//
// One row per filing the organization keeps: a library filing it confirmed, or one it recorded
// itself. Each row says what the organization RECORDED (the last filing or send, its number) and the
// next date the library worked out or the organization set — the composition is
// `compliance-filings-model.ts`'s, which reads a year from its cycle first and from the obligation
// row only where no cycle carries it (design go D1). The page never says a filing is owed, current
// or done with: a family shows only when a filing sits in it (§1.3: an empty "Tax filings" header
// would read as "nothing to file"), and no date here is computed in the browser.
//
// ⚠ NO NOT-A-SUBSTITUTE SENTENCE HERE. Row 7's sentence (D-CCM-19 §3) sits under a worked-out date
// with its basis — the panel, the add sheet, the Calendar's foot — and the list shows neither a basis
// nor a source, so it carries no sentence (`filings-page.test.tsx` pins its absence).
//
// ══ GROUPS ═════════════════════════════════════════════════════════════════════════════════════
//
// The organization's groups (CCM-R1-FILING-GROUPS) in its order: the six families plus any it made.
// Flat exactly when one group holds filings (§2.2). Anyone with the module's write string organizes
// them from two small menus (§2.3); a viewer sees the same structure, with "Open" and "Copy link"
// (§2.4). Which groups a person collapsed is that person's, kept in this browser per organization —
// a convenience with no backend, safe to lose.

import { useCallback, useEffect, useState, type FormEvent, type MouseEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  Button,
  Card,
  Dialog,
  EmptyState,
  FOCUS_RING,
  Field,
  Icon,
  IconButton,
  Menu,
  PageHeader,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  cn,
  testIdProp,
  toast,
} from '@givmo/design-system';
import {
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  FileText,
  MoreHorizontal,
} from 'lucide-react';
import { displayComplianceDate, serverMessage } from './compliance-decode';
import { DATE_PASSED, FIRST_PERIOD_WAITS_CELL, OVERDUE } from './compliance-panel-model';
import type { RenewalCycle } from './compliance-filings-decode';
import { useCompliance } from './compliance-context';
import {
  anchorKindFor,
  summarizeFilingGroup,
  type FilingGroupView,
  type FilingRow,
  type NextDate,
  type RecordedAct,
} from './compliance-filings-model';
import { filingGroupsQueryKey, useFilingsComposition } from './compliance-filings-reads';
import {
  followThroughSentence,
  isException,
  lastSendFollowThrough,
  type FollowThroughLine,
} from './compliance-results-model';
import { ModuleUnavailableNotice } from './compliance-shared';
import { COMPLIANCE_PAGE_TEST_IDS, COMPLIANCE_ROUTES } from './compliance-routes';
import { AddFilingButton, AddFilingSheet } from './add-filing-sheet';
import { UploadDocumentsButton } from './documents-page';
import { UploadDocumentsSheet } from './upload-sheet';
import { FilingPanel } from './filing-panel';
import { FirstVisit } from './first-visit';
import { withLinkedDate } from './compliance-vendor-model';
import {
  AddVendorRecordSheet,
  RECORD_SEARCH_KEY,
  VendorRecordPanel,
  VendorRecordsSection,
  useVendorRecords,
} from './vendor-records';

const PREFIX = 'compliance-filings';

// ── What this browser remembers per person: the collapsed groups ──────────────────────────────

const COLLAPSED_STORAGE_PREFIX = 'givmo.compliance.filings.collapsed';

function readCollapsed(storageKey: string | null): ReadonlySet<string> {
  if (storageKey === null) return new Set();
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(storageKey) ?? '[]');
    return new Set(Array.isArray(raw) ? raw.filter((key) => typeof key === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeCollapsed(storageKey: string | null, keys: ReadonlySet<string>): void {
  if (storageKey === null) return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify([...keys]));
  } catch {
    // A browser that keeps nothing (private window, blocked storage) forgets on reload; the page
    // works the same.
  }
}

/** Which groups this person collapsed, per organization; nothing is kept without a viewer key. */
function useCollapsedGroups(orgRef: string | null, viewerKey: string | undefined) {
  const storageKey =
    orgRef !== null && viewerKey !== undefined
      ? `${COLLAPSED_STORAGE_PREFIX}:${orgRef}:${viewerKey}`
      : null;
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => readCollapsed(storageKey));
  useEffect(() => {
    setCollapsed(readCollapsed(storageKey));
  }, [storageKey]);
  const toggle = useCallback(
    (groupKey: string) => {
      setCollapsed((previous) => {
        const next = new Set(previous);
        if (next.has(groupKey)) next.delete(groupKey);
        else next.add(groupKey);
        writeCollapsed(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );
  return { collapsed, toggle };
}

// ── Words ──────────────────────────────────────────────────────────────────────────────────────

/** "Maryland · Annual report": a filing named as the panel and the dialogs name it. */
function filingName(row: FilingRow): string {
  return row.subtitle !== undefined ? `${row.headline} · ${row.subtitle}` : row.headline;
}

function summaryParts(rows: readonly FilingRow[]): string[] {
  const summary = summarizeFilingGroup(rows);
  return [
    `${summary.count} ${summary.count === 1 ? 'filing' : 'filings'}`,
    ...(summary.nextDueOn !== undefined
      ? [`Next ${displayComplianceDate(summary.nextDueOn)}`]
      : []),
    ...(summary.needsInput > 0 ? [`${summary.needsInput} needs your input`] : []),
  ];
}

/** The words for a next date that is not a date (frames 13–15; the 18:00 PDT copy ruling). */
function nextDateWords(next: NextDate): string | undefined {
  switch (next.kind) {
    case 'dated':
      return undefined;
    case 'needs-date':
      // Florida's "approval date" is per-rule wording no producer serves yet (CCM-R1-FILING-META-2);
      // a licence's expiry is the one anchor the list can name without it.
      return anchorKindFor(next.dueDateBasis) === 'license_expiry'
        ? 'Needs the expiration date'
        : 'Needs a date from you';
    case 'needs-fiscal-year-end':
      return 'Needs your fiscal year end';
    case 'before-first-solicitation':
      return 'Before first soliciting';
    case 'filed-once':
      return 'Filed once';
    case 'not-supported':
      return 'Not supported yet';
    case 'no-longer-tracked':
      return 'No longer tracked';
    case 'first-period-waits':
      return FIRST_PERIOD_WAITS_CELL;
  }
}

/** Whether a not-a-date answer is the organization's to give (its words read stronger). */
function isQuestion(next: NextDate): boolean {
  return (
    next.kind === 'needs-date' ||
    next.kind === 'needs-fiscal-year-end' ||
    next.kind === 'before-first-solicitation' ||
    next.kind === 'first-period-waits'
  );
}

function lastActLine(act: RecordedAct | undefined, from: string | undefined): string {
  if (act === undefined) return 'Nothing recorded yet';
  const on = displayComplianceDate(act.on);
  if (act.kind === 'filed') return `Filed ${on}, confirmation on file`;
  return from !== undefined ? `Sent ${on}, waiting for ${from}’s confirmation` : `Sent ${on}`;
}

/**
 * What came back after a filing's latest send, where it speaks for the last act (CCM-R1-RESULTS-WEB,
 * U3; D-CCM-68): the Calendar's sentence for an exception, and "Sent again" for a send after another.
 */
function useLatestSend(row: FilingRow): FollowThroughLine | undefined {
  const { personName } = useCompliance();
  return lastSendFollowThrough(row.lastAct?.on, row.latestSend, (userId) =>
    userId !== undefined ? personName?.(userId) : undefined,
  );
}

// ── Cells ──────────────────────────────────────────────────────────────────────────────────────

function LastFilingCell({ row }: { row: FilingRow }) {
  const act = row.lastAct;
  const latest = useLatestSend(row);
  if (latest !== undefined && isException(latest)) {
    return (
      <span className="text-text" {...testIdProp(`${PREFIX}-last-exception-${row.key}`)}>
        {followThroughSentence(latest, row.confirmationFrom ?? row.headline)}
      </span>
    );
  }
  if (latest?.kind === 'waiting') {
    return (
      <div className="flex flex-col gap-2">
        <span>{latest.again ? `Sent again ${latest.sentOn}` : `Sent ${latest.sentOn}`}</span>
        {row.confirmationFrom !== undefined && (
          <span className="text-s12 text-text-tertiary">
            Waiting for {row.confirmationFrom}’s confirmation
          </span>
        )}
      </div>
    );
  }
  if (act === undefined) return <span className="text-text-tertiary">—</span>;
  const on = displayComplianceDate(act.on);
  if (act.kind === 'sent') {
    return (
      <div className="flex flex-col gap-2">
        <span>Sent {on}</span>
        {row.confirmationFrom !== undefined && (
          <span className="text-s12 text-text-tertiary">
            Waiting for {row.confirmationFrom}’s confirmation
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <span>Filed {on}</span>
      <span className="flex items-center gap-4 text-s12 text-text-tertiary">
        <Icon icon={FileText} size={12} aria-hidden />
        Confirmation on file
      </span>
    </div>
  );
}

function NextDateCell({
  next,
  overdue,
  testId,
}: {
  next: NextDate;
  /** CCM-R1-LATE-FILINGS (frame 1c): the filing's oldest year read overdue, which leads its row. */
  overdue?: RenewalCycle;
  testId: string;
}) {
  if (overdue?.dueOn !== undefined) {
    return (
      <div className="flex flex-col gap-2" {...testIdProp(testId)}>
        <span className="tabular-nums">{displayComplianceDate(overdue.dueOn)}</span>
        <span className="text-s12 text-text-secondary">{OVERDUE}</span>
      </div>
    );
  }
  if (next.kind === 'dated') {
    return next.own ? (
      <div className="flex flex-col gap-2" {...testIdProp(testId)}>
        <span className="tabular-nums">{displayComplianceDate(next.dueOn)}</span>
        {/* The organization's own date on a filing outside the library — said where the date is. */}
        <span className="text-s12 text-text-tertiary">Your date</span>
      </div>
    ) : next.datePassed === true ? (
      // MDEXT-OPEN: N1, while the year is held open by its automatic extension.
      <div className="flex flex-col gap-2" {...testIdProp(testId)}>
        <span className="tabular-nums">{displayComplianceDate(next.dueOn)}</span>
        <span className="text-s12 text-text-secondary">{DATE_PASSED}</span>
      </div>
    ) : (
      <span className="tabular-nums" {...testIdProp(testId)}>
        {displayComplianceDate(next.dueOn)}
      </span>
    );
  }
  return (
    <span
      className={isQuestion(next) ? 'text-text-secondary' : 'text-text-tertiary'}
      {...testIdProp(testId)}
    >
      {nextDateWords(next)}
    </span>
  );
}

// ── Menus ──────────────────────────────────────────────────────────────────────────────────────

interface RowActions {
  copyLink: (row: FilingRow) => void;
  move: (row: FilingRow, group: FilingGroupView) => void;
  newGroup: (row: FilingRow) => void;
  open: (row: FilingRow) => void;
}

/**
 * The row menu: copy the panel's link, and MOVE TO — the organization's groups as a radio set (the
 * current one checked, `menuitemradio` saying which), then "New group…" (frame 16). A viewer's menu,
 * and a read-only filing's, opens or copies; nothing else.
 */
function RowMenu({
  row,
  current,
  targets,
  canOrganize,
  actions,
  onOpenChange,
}: {
  row: FilingRow;
  current: FilingGroupView;
  targets: readonly FilingGroupView[];
  canOrganize: boolean;
  actions: RowActions;
  onOpenChange: (open: boolean) => void;
}) {
  const movable = canOrganize && !row.readOnly && current.groupId !== undefined;
  return (
    <Menu.Root modal={false} onOpenChange={onOpenChange}>
      <Menu.Trigger
        saas
        render={<IconButton size="sm" tone="ghost" aria-label={`Actions for ${filingName(row)}`} />}
        testId={`${PREFIX}-row-menu-${row.key}`}
      >
        <Icon icon={MoreHorizontal} size={16} aria-hidden />
      </Menu.Trigger>
      <Menu.Content saas className="min-w-[248px]">
        {!canOrganize && (
          <Menu.Item saas onClick={() => actions.open(row)}>
            Open
          </Menu.Item>
        )}
        <Menu.Item saas onClick={() => actions.copyLink(row)}>
          Copy link
        </Menu.Item>
        {movable && (
          <>
            <Menu.Separator className="mx-4 my-4 bg-border-subtle" />
            <Menu.Group>
              <Menu.GroupLabel saas>Move to</Menu.GroupLabel>
              <Menu.RadioGroup
                value={current.key}
                onValueChange={(value) => {
                  const target = targets.find((group) => group.key === value);
                  if (target !== undefined && target.key !== current.key) actions.move(row, target);
                }}
              >
                {targets.map((group) => (
                  <Menu.RadioItem
                    key={group.key}
                    saas
                    value={group.key}
                    endIcon={
                      group.key === current.key ? (
                        <Icon icon={Check} size={16} className="text-brand-600" aria-hidden />
                      ) : undefined
                    }
                  >
                    {group.label}
                  </Menu.RadioItem>
                ))}
              </Menu.RadioGroup>
              <Menu.Item saas onClick={() => actions.newGroup(row)}>
                New group…
              </Menu.Item>
            </Menu.Group>
          </>
        )}
      </Menu.Content>
    </Menu.Root>
  );
}

interface GroupActions {
  rename: (group: FilingGroupView) => void;
  moveGroup: (group: FilingGroupView, direction: 'up' | 'down') => void;
  remove: (group: FilingGroupView) => void;
}

/** The group header menu: rename, reorder, and — for a group the organization made — remove. */
function GroupMenu({
  group,
  first,
  last,
  actions,
}: {
  group: FilingGroupView;
  first: boolean;
  last: boolean;
  actions: GroupActions;
}) {
  return (
    <Menu.Root modal={false}>
      <Menu.Trigger
        saas
        render={<IconButton size="sm" tone="ghost" aria-label={`Options for ${group.label}`} />}
        testId={`${PREFIX}-group-menu-${group.key}`}
      >
        <Icon icon={MoreHorizontal} size={16} aria-hidden />
      </Menu.Trigger>
      <Menu.Content saas>
        <Menu.Item saas onClick={() => actions.rename(group)}>
          Rename
        </Menu.Item>
        <Menu.Item saas disabled={first} onClick={() => actions.moveGroup(group, 'up')}>
          Move up
        </Menu.Item>
        <Menu.Item saas disabled={last} onClick={() => actions.moveGroup(group, 'down')}>
          Move down
        </Menu.Item>
        {group.own && (
          <Menu.Item saas tone="danger" onClick={() => actions.remove(group)}>
            Remove group
          </Menu.Item>
        )}
      </Menu.Content>
    </Menu.Root>
  );
}

// ── The wide table ─────────────────────────────────────────────────────────────────────────────

const COLUMNS = 5;

function RenameForm({
  group,
  pending,
  onSave,
  onCancel,
}: {
  group: FilingGroupView;
  pending: boolean;
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(group.label);
  const trimmed = value.trim();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (trimmed.length === 0) return;
    if (trimmed === group.label) onCancel();
    else onSave(trimmed);
  };
  return (
    <form
      className="flex min-w-0 flex-1 items-center gap-8 pl-4"
      onSubmit={submit}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onCancel();
      }}
      {...testIdProp(`${PREFIX}-rename-${group.key}`)}
    >
      <Field
        aria-label="Group name"
        value={value}
        maxLength={80}
        autoFocus
        onChange={(event) => setValue(event.target.value)}
        containerClassName="w-[400px] max-w-full"
      />
      <Button
        appearance="saas"
        tone="primary"
        size="sm"
        type="submit"
        disabled={trimmed.length === 0 || pending}
      >
        Save
      </Button>
      <Button appearance="saas" tone="ghost" size="sm" type="button" onClick={onCancel}>
        Cancel
      </Button>
    </form>
  );
}

function GroupHeaderRow({
  group,
  collapsed,
  onToggle,
  first,
  last,
  canOrganize,
  renaming,
  renamePending,
  actions,
  onRenameSave,
  onRenameCancel,
}: {
  group: FilingGroupView;
  collapsed: boolean;
  onToggle: () => void;
  first: boolean;
  last: boolean;
  canOrganize: boolean;
  renaming: boolean;
  renamePending: boolean;
  actions: GroupActions;
  onRenameSave: (name: string) => void;
  onRenameCancel: () => void;
}) {
  const parts = summaryParts(group.rows);
  return (
    <tr
      className="group/band border-b border-border-subtle bg-neutral-50"
      {...testIdProp(`${PREFIX}-group-${group.key}`)}
    >
      <td colSpan={COLUMNS} className="h-[40px] px-12 py-4">
        <div className="flex items-center gap-12">
          {renaming ? (
            <RenameForm
              group={group}
              pending={renamePending}
              onSave={onRenameSave}
              onCancel={onRenameCancel}
            />
          ) : (
            <>
              <button
                type="button"
                aria-expanded={!collapsed}
                onClick={onToggle}
                className={cn(
                  'flex min-w-0 items-center gap-6 rounded-md py-4 pr-6 pl-4 text-left',
                  FOCUS_RING,
                )}
                {...testIdProp(`${PREFIX}-group-toggle-${group.key}`)}
              >
                <Icon
                  icon={collapsed ? ChevronRight : ChevronDown}
                  size={16}
                  className="shrink-0 text-icon-muted"
                  aria-hidden
                />
                <span className="truncate text-s14 font-w600 text-text-strong">{group.label}</span>
              </button>
              <span
                className="flex min-w-0 flex-wrap items-center gap-x-8 text-s13 text-text-tertiary"
                {...testIdProp(`${PREFIX}-group-summary-${group.key}`)}
              >
                {parts.map((part, index) => (
                  <span key={part} className="contents">
                    {index > 0 && <span aria-hidden>·</span>}
                    <span
                      className={
                        part.endsWith('needs your input') ? 'text-text-secondary' : 'tabular-nums'
                      }
                    >
                      {part}
                    </span>
                  </span>
                ))}
              </span>
              {/* Rename and reorder are occasional: the band's menu shows on hover or focus, and
                  always where there is no hover (a touch screen). */}
              {canOrganize && group.groupId !== undefined && (
                <div className="ml-auto shrink-0 opacity-0 transition-opacity group-hover/band:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100">
                  <GroupMenu group={group} first={first} last={last} actions={actions} />
                </div>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function FilingTableRow({
  row,
  current,
  targets,
  canOrganize,
  actions,
  selected,
}: {
  row: FilingRow;
  current: FilingGroupView;
  targets: readonly FilingGroupView[];
  canOrganize: boolean;
  actions: RowActions;
  /** The row whose panel is open — kept selected behind the sheet, as a list keeps its place. */
  selected: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  // The whole row opens the panel; the headline is its link, so a keyboard reaches it too. A click
  // that lands on the link, the menu or anything the menu renders is theirs (a portal's events
  // still bubble through the React tree).
  const onRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement;
    if (
      target.closest('a, button, input, [role="menu"], [role="menuitem"], [role="menuitemradio"]')
    )
      return;
    actions.open(row);
  };
  return (
    <TR
      interactive
      className={selected ? 'bg-surface-active' : menuOpen ? 'bg-surface-hover' : undefined}
      aria-selected={selected || undefined}
      onClick={onRowClick}
      testId={`${PREFIX}-row-${row.key}`}
    >
      <TD className="py-[9px]">
        <div className="flex flex-col gap-2">
          <Link
            to={COMPLIANCE_ROUTES.filing(row.ref.id)}
            className={cn('w-fit rounded-xs font-w600 text-text-strong', FOCUS_RING)}
          >
            {row.headline}
          </Link>
          {row.subtitle !== undefined && (
            <span className="text-s12 text-text-tertiary">{row.subtitle}</span>
          )}
        </div>
      </TD>
      <TD numeric>{row.number ?? <span className="text-text-tertiary">—</span>}</TD>
      <TD className="py-[9px]">
        <LastFilingCell row={row} />
      </TD>
      <TD className="py-[9px]">
        <NextDateCell
          next={row.next}
          overdue={row.overdue?.[0]}
          testId={`${PREFIX}-next-${row.key}`}
        />
      </TD>
      <TD className="w-[56px] pr-12 pl-0 text-right">
        <RowMenu
          row={row}
          current={current}
          targets={targets}
          canOrganize={canOrganize}
          actions={actions}
          onOpenChange={setMenuOpen}
        />
      </TD>
    </TR>
  );
}

// ── The phone layout: the same rows, stacked, under the same group headers ──────────────────────

function StackedRow({ row }: { row: FilingRow }) {
  const latest = useLatestSend(row);
  const from = row.confirmationFrom ?? row.headline;
  const exception = latest !== undefined && isException(latest);
  const last =
    latest === undefined
      ? lastActLine(row.lastAct, row.confirmationFrom)
      : latest.kind === 'waiting'
        ? `${latest.again ? 'Sent again' : 'Sent'} ${latest.sentOn}, waiting for ${from}’s confirmation`
        : followThroughSentence(latest, from);
  return (
    <li className="border-b border-border-subtle last:border-b-0">
      <Link
        to={COMPLIANCE_ROUTES.filing(row.ref.id)}
        className={cn('flex flex-col gap-4 px-16 py-14', FOCUS_RING)}
        {...testIdProp(`${PREFIX}-stacked-${row.key}`)}
      >
        <div className="flex items-start justify-between gap-12">
          <div className="flex min-w-0 flex-col gap-2">
            <span className="text-s14 font-w600 text-text-strong">{row.headline}</span>
            {row.subtitle !== undefined && (
              <span className="text-s12 text-text-tertiary">{row.subtitle}</span>
            )}
          </div>
          <div className="shrink-0 text-right text-s14 text-text">
            <NextDateCell
              next={row.next}
              overdue={row.overdue?.[0]}
              testId={`${PREFIX}-stacked-next-${row.key}`}
            />
          </div>
        </div>
        <span className={cn('text-s12', exception ? 'text-text' : 'text-text-tertiary')}>
          {last}
          {row.number !== undefined ? ` · No. ${row.number}` : ''}
        </span>
      </Link>
    </li>
  );
}

// ── Dialogs ────────────────────────────────────────────────────────────────────────────────────

function NewGroupDialog({
  row,
  pending,
  onCreate,
  onClose,
}: {
  row: FilingRow | null;
  pending: boolean;
  onCreate: (row: FilingRow, name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  useEffect(() => {
    if (row !== null) setName('');
  }, [row]);
  const trimmed = name.trim();
  return (
    <Dialog.Root
      open={row !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {row !== null && (
        <Dialog.Content
          width={440}
          className="flex flex-col gap-16"
          testId={`${PREFIX}-new-group-dialog`}
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            New group
          </Dialog.Title>
          <form
            className="flex flex-col gap-16"
            onSubmit={(event) => {
              event.preventDefault();
              if (trimmed.length > 0) onCreate(row, trimmed);
            }}
          >
            <Field
              label="Name"
              value={name}
              maxLength={80}
              autoFocus
              onChange={(event) => setName(event.target.value)}
              helper={`${filingName(row)} moves into it. You can move other filings in later.`}
            />
            <div className="flex justify-end gap-10">
              <Button appearance="saas" tone="secondary" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                appearance="saas"
                tone="primary"
                type="submit"
                disabled={trimmed.length === 0 || pending}
              >
                Create group
              </Button>
            </div>
          </form>
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
}

function RemoveGroupDialog({
  group,
  pending,
  onRemove,
  onClose,
}: {
  group: FilingGroupView | null;
  pending: boolean;
  onRemove: (group: FilingGroupView) => void;
  onClose: () => void;
}) {
  return (
    <Dialog.Root
      open={group !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {group !== null && (
        <Dialog.Content
          width={440}
          className="flex flex-col gap-16"
          testId={`${PREFIX}-remove-group-dialog`}
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            Remove {group.label}?
          </Dialog.Title>
          <Dialog.Description className="text-s14 text-text">
            Its filings go back to their own groups.
          </Dialog.Description>
          <div className="flex justify-end gap-10">
            <Button appearance="saas" tone="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              appearance="saas"
              tone="danger"
              type="button"
              disabled={pending}
              onClick={() => onRemove(group)}
            >
              Remove
            </Button>
          </div>
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
}

// ── The page ───────────────────────────────────────────────────────────────────────────────────

export interface FilingsPageProps {
  /**
   * What an open panel shows: the filing (`/compliance/filings/<id>`), or the rule behind it
   * (`/compliance/filings/<id>/rule`, design go D2). The host's route says which.
   */
  panel?: 'filing' | 'rule';
}

export function FilingsPage({ panel = 'filing' }: FilingsPageProps = {}) {
  const {
    orgRef,
    isReady,
    isPending: orgPending,
    isError: orgError,
    error,
    operations,
    moduleStanding,
    canWrite,
    viewerKey,
  } = useCompliance();
  const available = moduleStanding === 'available';
  const ref = isReady && available ? orgRef : null;
  const composition = useFilingsComposition(operations, ref);
  // `/compliance/filings/<filing id>` opens that filing's panel over the list (DESIGN-NOTE-1b §4.1).
  const panelFilingId = (useParams({ strict: false }) as { filingId?: string }).filingId;
  // `/compliance/filings?record=<id>` opens one of the organization's registered agents or foreign
  // qualifications over the list (CCM-MOD5B; R14: a search key, so no route identity moves).
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const panelRecordId =
    typeof search[RECORD_SEARCH_KEY] === 'string'
      ? (search[RECORD_SEARCH_KEY] as string)
      : undefined;
  const vendorRecords = useVendorRecords();
  const { failed, moduleDark, loading } = composition;
  // L6 (D-CCM-97): a linked record's date fills its filing's row, and the group's "Next" counts it.
  const linkOf = (obligationId: string) =>
    composition.records?.obligations.find((obligation) => obligation.obligationId === obligationId)
      ?.vendorLink;
  const allGroups = composition.groups?.map((group) => ({
    ...group,
    rows: group.rows.map((row) => withLinkedDate(row, linkOf)),
  }));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { collapsed, toggle } = useCollapsedGroups(ref, viewerKey);
  const canOrganize = canWrite === true;
  const [adding, setAdding] = useState(false);
  const [addingRecord, setAddingRecord] = useState(false);
  const [uploading, setUploading] = useState(false);
  const visible = (allGroups ?? []).filter((group) => group.rows.length > 0);
  const flat = visible.length === 1;

  const [renamingKey, setRenamingKey] = useState<string | null>(null);
  const [newGroupFor, setNewGroupFor] = useState<FilingRow | null>(null);
  const [removing, setRemoving] = useState<FilingGroupView | null>(null);

  const refreshGroups = () =>
    queryClient.invalidateQueries({ queryKey: filingGroupsQueryKey(ref) });

  const moveFiling = useMutation({
    mutationFn: ({ row, group }: { row: FilingRow; group: FilingGroupView }) =>
      operations.moveFilingToGroup(ref ?? '', group.groupId ?? '', {
        filing_kind: row.ref.kind,
        filing_id: row.ref.id,
      }),
    onSuccess: (_body, { group }) => {
      toast.success(`Moved to ${group.label}.`);
      void refreshGroups();
    },
    onError: (cause) =>
      toast.error(serverMessage(cause, 'We couldn’t move that filing. Please try again.')),
  });
  const createGroup = useMutation({
    mutationFn: ({ row, name }: { row: FilingRow; name: string }) =>
      operations.createFilingGroup(ref ?? '', {
        name,
        filing_kind: row.ref.kind,
        filing_id: row.ref.id,
      }),
    onSuccess: () => {
      toast.success('Group created.');
      setNewGroupFor(null);
      void refreshGroups();
    },
    onError: (cause) =>
      toast.error(serverMessage(cause, 'We couldn’t create that group. Please try again.')),
  });
  const renameGroup = useMutation({
    mutationFn: ({ group, name }: { group: FilingGroupView; name: string }) =>
      operations.renameFilingGroup(ref ?? '', group.groupId ?? '', { name }),
    onSuccess: () => {
      toast.success('Group renamed.');
      setRenamingKey(null);
      void refreshGroups();
    },
    onError: (cause) =>
      toast.error(serverMessage(cause, 'We couldn’t rename that group. Please try again.')),
  });
  const reorderGroup = useMutation({
    mutationFn: ({ group, direction }: { group: FilingGroupView; direction: 'up' | 'down' }) =>
      operations.moveFilingGroup(ref ?? '', group.groupId ?? '', { direction }),
    onSuccess: () => void refreshGroups(),
    onError: (cause) =>
      toast.error(serverMessage(cause, 'We couldn’t move that group. Please try again.')),
  });
  const removeGroup = useMutation({
    mutationFn: (group: FilingGroupView) =>
      operations.removeFilingGroup(ref ?? '', group.groupId ?? ''),
    onSuccess: () => {
      toast.success('Group removed.');
      setRemoving(null);
      void refreshGroups();
    },
    onError: (cause) =>
      toast.error(serverMessage(cause, 'We couldn’t remove that group. Please try again.')),
  });

  const rowActions: RowActions = {
    open: (row) => void navigate({ to: COMPLIANCE_ROUTES.filing(row.ref.id) }),
    copyLink: (row) => {
      const link = new URL(COMPLIANCE_ROUTES.filing(row.ref.id), window.location.origin).href;
      const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
      if (clipboard === undefined) {
        toast.error('We couldn’t copy the link. Please try again.');
        return;
      }
      clipboard.writeText(link).then(
        () => toast.success('Link copied.'),
        () => toast.error('We couldn’t copy the link. Please try again.'),
      );
    },
    move: (row, group) => moveFiling.mutate({ row, group }),
    newGroup: (row) => setNewGroupFor(row),
  };
  const groupActions: GroupActions = {
    rename: (group) => setRenamingKey(group.key),
    moveGroup: (group, direction) => reorderGroup.mutate({ group, direction }),
    remove: (group) => setRemoving(group),
  };

  /** Where a filing may move: the groups on the page, and its own family's when that one is hidden. */
  const targetsFor = (row: FilingRow): FilingGroupView[] =>
    (allGroups ?? []).filter(
      (group) =>
        group.groupId !== undefined &&
        (group.rows.length > 0 || (group.family === row.family && !group.own)),
    );

  const panelElement = panelFilingId !== undefined && isReady && available && (
    <FilingPanel
      filingId={panelFilingId}
      view={panel}
      onClose={() => void navigate({ to: COMPLIANCE_ROUTES.filings })}
      onNavigate={(to, search) =>
        void navigate({ to, ...(search !== undefined ? { search } : {}) })
      }
    />
  );

  // A record's panel and its add sheet (CCM-MOD5B), beside the filing's.
  const openRecord = (vendorId: string) =>
    void navigate({ to: COMPLIANCE_ROUTES.filings, search: { [RECORD_SEARCH_KEY]: vendorId } });
  /** A library filing's title as the page names it ("District of Columbia · …"), for V8a. */
  const filingTitleOf = (obligationId: string) => {
    const row = composition.rows?.find((candidate) => candidate.ref.id === obligationId);
    if (row === undefined) return undefined;
    return row.subtitle !== undefined ? `${row.headline} · ${row.subtitle}` : row.headline;
  };
  const recordPanelElement = panelRecordId !== undefined && isReady && available && (
    <VendorRecordPanel
      vendorId={panelRecordId}
      filingTitle={filingTitleOf}
      onClose={() => void navigate({ to: COMPLIANCE_ROUTES.filings })}
      onOpenFiling={(obligationId) => void navigate({ to: COMPLIANCE_ROUTES.filing(obligationId) })}
    />
  );
  const recordSheetElement = canOrganize && (
    <AddVendorRecordSheet
      open={addingRecord}
      onClose={() => setAddingRecord(false)}
      onAdded={(vendorId) => {
        setAddingRecord(false);
        openRecord(vendorId);
      }}
      onOpenRecord={(vendorId) => {
        setAddingRecord(false);
        openRecord(vendorId);
      }}
    />
  );

  // The add sheet, for a writer; the filing it adds opens in its panel.
  const sheetElement = canOrganize && (
    <AddFilingSheet
      open={adding}
      onClose={() => setAdding(false)}
      onAdded={(filingId) => {
        setAdding(false);
        void navigate({ to: COMPLIANCE_ROUTES.filing(filingId) });
      }}
    />
  );

  const uploadElement = canOrganize && (
    <UploadDocumentsSheet open={uploading} onClose={() => setUploading(false)} />
  );

  // Nothing recorded: the first visit stands in for an empty list (copy ruling item 6). A registered
  // agent or a foreign qualification is something recorded (R8), so the first visit waits on their
  // read too; a failed read of them counts as none, so it never holds the first visit back.
  const recordsKnown = vendorRecords.data !== undefined || vendorRecords.isError;
  const recordsHeld = (vendorRecords.data?.length ?? 0) > 0;
  const filingsSettled = isReady && available && !moduleDark && failed === undefined && !loading;
  const firstVisit =
    filingsSettled && composition.rows?.length === 0 && recordsKnown && !recordsHeld;
  const waitingOnRecords = filingsSettled && composition.rows?.length === 0 && !recordsKnown;

  const page = firstVisit ? (
    <FirstVisit
      pageTestId={COMPLIANCE_PAGE_TEST_IDS.filings}
      {...(canOrganize
        ? {
            onAddFiling: () => setAdding(true),
            onUpload: () => setUploading(true),
            // D2 (D-CCM-97): the door opens V3's sheet, the records section's own.
            onAddRecord: () => setAddingRecord(true),
          }
        : {})}
    />
  ) : (
    <div
      className="flex w-full max-w-[1120px] flex-col gap-28 px-16 py-28 sm:px-24 lg:px-32"
      {...testIdProp(COMPLIANCE_PAGE_TEST_IDS.filings)}
    >
      <PageHeader
        title="Filings"
        {...(canOrganize
          ? {
              actions: (
                <>
                  <UploadDocumentsButton onClick={() => setUploading(true)} />
                  <AddFilingButton onClick={() => setAdding(true)} />
                </>
              ),
            }
          : {})}
      />

      {orgError && (
        <EmptyState
          testId={`${PREFIX}-org-error`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Couldn’t load your organization"
          description={serverMessage(error, 'Please try again.')}
        />
      )}

      {!orgError && !isReady && orgPending && <Skeleton className="h-[320px] w-full" />}

      {isReady && available && moduleDark && (
        <ModuleUnavailableNotice testId={`${PREFIX}-unavailable`} />
      )}

      {isReady && available && !moduleDark && failed !== undefined && (
        <EmptyState
          testId={`${PREFIX}-error`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Couldn’t load your filings"
          description={serverMessage(failed, 'Please try again.')}
        />
      )}

      {isReady &&
        available &&
        !moduleDark &&
        failed === undefined &&
        (loading || waitingOnRecords) && (
          <Skeleton className="h-[320px] w-full" {...testIdProp(`${PREFIX}-loading`)} />
        )}

      {visible.length > 0 && (
        <>
          {/* Wide screens: one table, one column header, a band per group. */}
          <Card className="hidden overflow-hidden md:block" testId={`${PREFIX}-table`}>
            {/* Fixed column widths: collapsing or expanding a group must never reflow the columns. */}
            <Table className="table-fixed">
              <THead className={flat ? undefined : 'bg-surface'}>
                <TR>
                  <TH className="w-[32%]">Filing</TH>
                  <TH className="w-[15%]">Number</TH>
                  <TH className="w-[25%]">Last filing</TH>
                  <TH className="w-[22%]">Next date</TH>
                  <TH className="w-[56px]">
                    <span className="sr-only">Actions</span>
                  </TH>
                </TR>
              </THead>
              <TBody>
                {visible.map((group, index) => {
                  const isCollapsed = !flat && collapsed.has(group.key);
                  return (
                    <GroupBand
                      key={group.key}
                      group={group}
                      flat={flat}
                      collapsed={isCollapsed}
                      onToggle={() => toggle(group.key)}
                      first={index === 0}
                      last={index === visible.length - 1}
                      canOrganize={canOrganize}
                      renaming={renamingKey === group.key}
                      renamePending={renameGroup.isPending}
                      groupActions={groupActions}
                      onRenameSave={(name) => renameGroup.mutate({ group, name })}
                      onRenameCancel={() => setRenamingKey(null)}
                      rowActions={rowActions}
                      targetsFor={targetsFor}
                      selectedId={panelFilingId}
                    />
                  );
                })}
              </TBody>
            </Table>
          </Card>

          {/* Phone: stacked rows under the same group headers. */}
          <div className="flex flex-col gap-20 md:hidden" {...testIdProp(`${PREFIX}-stacked`)}>
            {visible.map((group) => {
              const isCollapsed = !flat && collapsed.has(group.key);
              return (
                <section key={group.key} className="flex flex-col gap-8">
                  {!flat && (
                    <div className="flex flex-col gap-2 px-4">
                      <h2>
                        <button
                          type="button"
                          aria-expanded={!isCollapsed}
                          onClick={() => toggle(group.key)}
                          className={cn(
                            'flex items-center gap-6 rounded-md text-left text-s14 font-w600 text-text-strong',
                            FOCUS_RING,
                          )}
                        >
                          <Icon
                            icon={isCollapsed ? ChevronRight : ChevronDown}
                            size={16}
                            className="text-icon-muted"
                            aria-hidden
                          />
                          {group.label}
                        </button>
                      </h2>
                      <p className="pl-22 text-s12 text-text-tertiary">
                        {summaryParts(group.rows).join(' · ')}
                      </p>
                    </div>
                  )}
                  {!isCollapsed && (
                    <Card className="overflow-hidden">
                      <ul>
                        {group.rows.map((row) => (
                          <StackedRow key={row.key} row={row} />
                        ))}
                      </ul>
                    </Card>
                  )}
                </section>
              );
            })}
          </div>
        </>
      )}

      {/* J1 (A): the organization's registered agents and foreign qualifications, after the families. */}
      {filingsSettled && !waitingOnRecords && (
        <VendorRecordsSection
          canWrite={canOrganize}
          selectedId={panelRecordId}
          onOpen={openRecord}
          onAdd={() => setAddingRecord(true)}
        />
      )}

      <NewGroupDialog
        row={newGroupFor}
        pending={createGroup.isPending}
        onCreate={(row, name) => createGroup.mutate({ row, name })}
        onClose={() => setNewGroupFor(null)}
      />
      <RemoveGroupDialog
        group={removing}
        pending={removeGroup.isPending}
        onRemove={(group) => removeGroup.mutate(group)}
        onClose={() => setRemoving(null)}
      />
    </div>
  );

  // The panel and both sheets keep ONE place in the tree, beside the page rather than inside it:
  // "Add a filing" is offered while the reads load, and when they settle with nothing recorded the
  // list gives way to the first visit. Inside the page, that switch would remount a sheet opened
  // meanwhile and lose what was typed in it (CCM-R1-INTAKE-SHEET-REMOUNT).
  return (
    <>
      {page}
      {panelElement}
      {recordPanelElement}
      {sheetElement}
      {recordSheetElement}
      {uploadElement}
    </>
  );
}

function GroupBand({
  group,
  flat,
  collapsed,
  onToggle,
  first,
  last,
  canOrganize,
  renaming,
  renamePending,
  groupActions,
  onRenameSave,
  onRenameCancel,
  rowActions,
  targetsFor,
  selectedId,
}: {
  group: FilingGroupView;
  flat: boolean;
  collapsed: boolean;
  onToggle: () => void;
  first: boolean;
  last: boolean;
  canOrganize: boolean;
  renaming: boolean;
  renamePending: boolean;
  groupActions: GroupActions;
  onRenameSave: (name: string) => void;
  onRenameCancel: () => void;
  rowActions: RowActions;
  targetsFor: (row: FilingRow) => FilingGroupView[];
  selectedId?: string;
}) {
  return (
    <>
      {!flat && (
        <GroupHeaderRow
          group={group}
          collapsed={collapsed}
          onToggle={onToggle}
          first={first}
          last={last}
          canOrganize={canOrganize}
          renaming={renaming}
          renamePending={renamePending}
          actions={groupActions}
          onRenameSave={onRenameSave}
          onRenameCancel={onRenameCancel}
        />
      )}
      {!collapsed &&
        group.rows.map((row) => (
          <FilingTableRow
            key={row.key}
            row={row}
            current={group}
            targets={targetsFor(row)}
            canOrganize={canOrganize}
            actions={rowActions}
            selected={row.ref.id === selectedId}
          />
        ))}
    </>
  );
}
